import { createStore } from 'vuex';
import { 
  getUserProfile, 
  getGlobalSettings, 
} from '@/composables/useFirestore';
import { auth } from '@/firebase/init';
import { db } from '@/firebase/init';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import lemonSqueezy from './modules/lemonSqueezy';
import products from './products';

export default createStore({
  modules: {
    lemonSqueezy,
    products
  },
  state: {
    user: null,
    profile: null,
    globalSettings: null,
    loading: false,
    alert: null,
    alertType: null,
    lastFetchTimestamps: {},
    dataRefreshFlags: {}
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
    },
    setProfile(state, profile) {
      state.profile = profile;
    },
    setGlobalSettings(state, settings) {
      state.globalSettings = settings;
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setAlert(state, { message, type }) {
      state.alert = message;
      state.alertType = type;
    },
    clearAlert(state) {
      state.alert = null;
      state.alertType = null;
    },
    setLastFetchTimestamp(state, { dataType, timestamp }) {
      state.lastFetchTimestamps[dataType] = timestamp;
    },
    setDataRefreshFlag(state, { dataType, value }) {
      state.dataRefreshFlags[dataType] = value;
    },
  },
  actions: {
    async fetchUser({ commit }) {
      commit('setLoading', true);
      return new Promise((resolve, reject) => {
        onAuthStateChanged(auth, async (user) => {
          if (user) {
            console.log(`[Store] User authenticated, fetching user data for: ${user.uid}`);
            commit('setUser', { uid: user.uid, email: user.email });
            try {
              const profile = await getUserProfile(user.uid);
              console.log('[Store] User profile fetched and stored in state');
              commit('setProfile', profile);
              resolve(profile);
            } catch (error) {
              console.error("Error fetching user profile:", error);
              reject(error);
            }
          } else {
            console.log('[Store] No authenticated user found');
            commit('setUser', null);
            commit('setProfile', null);
            resolve(null);
          }
          commit('setLoading', false);
        });
      });
    },

    async fetchGlobalSettings({ commit }) {
      commit('setLoading', true);
      try {
        console.log('[Store] Fetching global settings');
        const settings = await getGlobalSettings();
        if (settings) {
          console.log('[Store] Global settings fetched and stored in state');
          commit('setGlobalSettings', settings);
        }
      } catch (error) {
        console.error("Error fetching global settings:", error);
      } finally {
        commit('setLoading', false);
      }
    },

    async fetchUserProfile({ commit }) {
      try {
        if (!auth.currentUser) {
          console.log('[Store] No current user, skipping profile fetch');
          return;
        }

        commit('setLoading', true);
        
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        
        if (userDoc.exists()) {
          const profileData = userDoc.data();
          commit('setProfile', profileData);
          console.log('[Store] User profile fetched and stored in state:', profileData);
        } else {
          console.warn('[Store] User profile document does not exist for:', auth.currentUser.uid);
          // Don't throw error, just log warning
          commit('setProfile', null);
        }
      } catch (error) {
        console.error('[Store] Error fetching user profile:', error);
        // Don't throw error to prevent app crashes
        commit('setProfile', null);
      } finally {
        commit('setLoading', false);
      }
    },

    // Add an action to mark data as needing refresh
    markDataForRefresh({ commit }, dataType) {
      commit('setDataRefreshFlag', { dataType, value: true });
    },

    // When profile changes, recalculate limits
    setProfile({ commit }, profile) {
      commit('setProfile', profile);
      console.log('[Store] Profile set directly in state:', profile);
    },

    showAlert({ commit }, { message, type = 'info' }) {
      commit('setAlert', { message, type });
      setTimeout(() => commit('clearAlert'), 5000);
    },
  },
  getters: {
    userData: (state) => state.user,
  }
});